import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Parse HTML table from string
 */
const parseHTMLTable = (htmlString) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const table = doc.querySelector('table');
  
  if (!table) return null;

  const headers = [];
  const rows = [];

  // Extract headers
  const thead = table.querySelector('thead');
  if (thead) {
    const headerRow = thead.querySelector('tr');
    if (headerRow) {
      headerRow.querySelectorAll('th, td').forEach(cell => {
        headers.push(cell.textContent.trim());
      });
    }
  }

  // If no thead, try first row of tbody
  if (headers.length === 0) {
    const firstRow = table.querySelector('tr');
    if (firstRow) {
      const cells = firstRow.querySelectorAll('th, td');
      const isHeader = Array.from(cells).some(cell => cell.tagName === 'TH');
      if (isHeader) {
        cells.forEach(cell => headers.push(cell.textContent.trim()));
      }
    }
  }

  // Extract body rows
  const tbody = table.querySelector('tbody') || table;
  const rowElements = tbody.querySelectorAll('tr');
  
  rowElements.forEach((row, idx) => {
    // Skip first row if it was used as header
    if (headers.length > 0 && idx === 0 && !table.querySelector('thead')) {
      const cells = row.querySelectorAll('th, td');
      if (Array.from(cells).some(cell => cell.tagName === 'TH')) {
        return;
      }
    }

    const rowData = [];
    row.querySelectorAll('td, th').forEach(cell => {
      rowData.push(cell.textContent.trim());
    });
    
    if (rowData.length > 0) {
      rows.push(rowData);
    }
  });

  return { headers, rows };
};

/**
 * Parse Markdown-style table from text (e.g., | col1 | col2 |)
 */
const parseMarkdownTable = (lines, startIdx) => {
  const tableLines = [];
  let idx = startIdx;
  
  // Collect all consecutive lines that look like table rows
  while (idx < lines.length) {
    const line = lines[idx].trim();
    if (!line || (line.indexOf('|') === -1 || line.split('|').filter(c => c.trim()).length < 2)) {
      break;
    }
    // Skip separator lines (e.g., |---|---|)
    if (!/^[|\s\-:]+$/.test(line)) {
      tableLines.push(line);
    }
    idx++;
  }
  
  if (tableLines.length === 0) {
    return { tableData: null, nextIdx: startIdx + 1 };
  }
  
  // Parse table data
  const headers = [];
  const rows = [];
  
  tableLines.forEach((line, lineIdx) => {
    const cells = line.split('|')
      .map(cell => cell.trim())
      .filter(cell => cell && cell !== '---' && !/^[-:]+$/.test(cell));
    
    if (cells.length > 0) {
      if (lineIdx === 0) {
        // First line is header
        headers.push(...cells);
      } else {
        // Subsequent lines are data rows
        rows.push(cells);
      }
    }
  });
  
  return {
    tableData: headers.length > 0 || rows.length > 0 ? { headers, rows } : null,
    nextIdx: idx
  };
};

/**
 * Download report as PDF - Client-side generation (no API call)
 */
export const downloadReportPDF = async (reportData) => {
  try {
    // Create new PDF document
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const maxWidth = pageWidth - (margin * 2);
    let yPosition = margin;

    // Helper to add new page if needed
    const checkPageBreak = (requiredSpace = 10) => {
      if (yPosition + requiredSpace > pageHeight - margin) {
        doc.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };

    // Helper to add text with wrapping
    const addText = (text, fontSize, isBold = false, color = [0, 0, 0]) => {
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', isBold ? 'bold' : 'normal');
      doc.setTextColor(...color);
      
      const lines = doc.splitTextToSize(text, maxWidth);
      lines.forEach(line => {
        checkPageBreak();
        doc.text(line, margin, yPosition);
        yPosition += fontSize * 0.5;
      });
      yPosition += 3;
    };

    // Title
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 62, 80);
    const titleLines = doc.splitTextToSize(reportData.title || 'Analysis Report', maxWidth);
    titleLines.forEach(line => {
      doc.text(line, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 12;
    });
    yPosition += 5;

    // Subtitle
    doc.setFontSize(14);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(127, 140, 141);
    doc.text('Comprehensive Analysis Report', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;

    // Metadata
    doc.setFontSize(10);
    const generatedAt = reportData.generated_at || new Date().toISOString();
    const mode = reportData.mode || 'standard';
    const sectionsCount = reportData.sections?.length || 0;
    const metaInfo = `Generated: ${generatedAt} | Mode: ${mode} | Sections: ${sectionsCount}`;
    doc.text(metaInfo, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;

    // Process sections
    if (reportData.sections && Array.isArray(reportData.sections)) {
      for (const section of reportData.sections) {
        checkPageBreak(20);
        
        // Section title
        const sectionTitle = (section.section_type || '').replace(/_/g, ' ').toUpperCase();
        const emojiMap = {
          'TECHNICAL REQUIREMENTS': '[TECH]',
          'MARKET ANALYSIS': '[MARKET]',
          'PERFORMANCE APPLICATIONS': '[PERF]',
          'REGULATORY COMPLIANCE': '[REG]',
          'FINANCIAL ANALYSIS': '[FIN]',
          'RISK ASSESSMENT': '[RISK]',
          'REFERENCES': '[REF]',
          'IDEA SUMMARY': '[SUMMARY]',
          'COMPETITOR ANALYSIS': '[COMP]',
          'RESEARCH ANALYSIS': '[RESEARCH]'
        };
        const indicator = emojiMap[sectionTitle] || '[INFO]';
        
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(44, 62, 80);
        doc.text(`${indicator} ${sectionTitle}`, margin, yPosition);
        yPosition += 10;

        // Section content
        const content = section.content || '';
        
        // Check if content contains HTML tables
        if (content.includes('<table')) {
          // Split content by tables to process mixed content
          const parts = content.split(/(<table[\s\S]*?<\/table>)/gi);
          
          for (const part of parts) {
            if (part.trim().startsWith('<table')) {
              // Render HTML table
              const tableData = parseHTMLTable(part);
              if (tableData && tableData.rows.length > 0) {
                checkPageBreak(30);
                
                autoTable(doc, {
                  startY: yPosition,
                  head: tableData.headers.length > 0 ? [tableData.headers] : undefined,
                  body: tableData.rows,
                  margin: { left: margin, right: margin },
                  theme: 'striped',
                  headStyles: {
                    fillColor: [52, 73, 94],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    fontSize: 10
                  },
                  bodyStyles: {
                    fontSize: 9,
                    textColor: [0, 0, 0]
                  },
                  alternateRowStyles: {
                    fillColor: [245, 245, 245]
                  }
                });
                
                yPosition = doc.lastAutoTable.finalY + 8;
              }
            } else if (part.trim()) {
              // Process regular text content (may contain markdown tables)
              const lines = part.split('\n');
              let lineIdx = 0;
              
              while (lineIdx < lines.length) {
                const line = lines[lineIdx];
                const trimmed = line.trim();
                
                if (!trimmed) {
                  yPosition += 3;
                  lineIdx++;
                  continue;
                }

                // Check for markdown table (lines with | separator)
                if (trimmed.includes('|') && trimmed.split('|').filter(c => c.trim()).length > 1) {
                  const { tableData, nextIdx } = parseMarkdownTable(lines, lineIdx);
                  
                  if (tableData && (tableData.headers.length > 0 || tableData.rows.length > 0)) {
                    checkPageBreak(30);
                    
                    autoTable(doc, {
                      startY: yPosition,
                      head: tableData.headers.length > 0 ? [tableData.headers] : undefined,
                      body: tableData.rows,
                      margin: { left: margin, right: margin },
                      theme: 'striped',
                      headStyles: {
                        fillColor: [52, 73, 94],
                        textColor: [255, 255, 255],
                        fontStyle: 'bold',
                        fontSize: 10
                      },
                      bodyStyles: {
                        fontSize: 9,
                        textColor: [0, 0, 0]
                      },
                      alternateRowStyles: {
                        fillColor: [245, 245, 245]
                      }
                    });
                    
                    yPosition = doc.lastAutoTable.finalY + 8;
                    lineIdx = nextIdx;
                    continue;
                  }
                }

                checkPageBreak();

                // Skip separator lines
                if (/^[=-]+$/.test(trimmed)) {
                  lineIdx++;
                  lineIdx++;
                  continue;
                }

                // Headers (###, **)
                if (trimmed.startsWith('###') || (trimmed.startsWith('**') && trimmed.endsWith('**'))) {
                  const headerText = trimmed.replace(/^###\s*/, '').replace(/^\*\*|\*\*$/g, '')
                    .replace(/[ØÜ¡ß]/g, '')
                    .replace(/[^\x20-\x7E\n\r\t]/g, '');
                  addText(headerText, 12, true, [52, 73, 94]);
                  lineIdx++;
                }
                // Bullet points
                else if (trimmed.startsWith('•') || trimmed.startsWith('-') || trimmed.startsWith('*')) {
                  const bulletText = trimmed.substring(1).trim();
                  const cleanText = bulletText.replace(/\*\*/g, '').replace(/https?:\/\/[^\s]+/g, '[URL]')
                    .replace(/[ØÜ¡ß]/g, '')
                    .replace(/[^\x20-\x7E\n\r\t]/g, '');
                  doc.setFontSize(11);
                  doc.setFont('helvetica', 'normal');
                  doc.setTextColor(0, 0, 0);
                  const bulletLines = doc.splitTextToSize(`• ${cleanText}`, maxWidth - 10);
                  let currentY = yPosition;
                  bulletLines.forEach((bLine, idx) => {
                    checkPageBreak();
                    doc.text(bLine, margin + (idx > 0 ? 12 : 5), currentY);
                    currentY += 5.5;
                  });
                  yPosition = currentY;
                  lineIdx++;
                }
                // Numbered lists
                else if (/^\d+\.\s/.test(trimmed)) {
                  const cleanText = trimmed.replace(/\*\*/g, '').replace(/https?:\/\/[^\s]+/g, '[URL]')
                    .replace(/[ØÜ¡ß]/g, '')
                    .replace(/[^\x20-\x7E\n\r\t]/g, '');
                  doc.setFontSize(11);
                  doc.setFont('helvetica', 'normal');
                  doc.setTextColor(0, 0, 0);
                  const numLines = doc.splitTextToSize(cleanText, maxWidth - 10);
                  let currentY = yPosition;
                  numLines.forEach(nLine => {
                    checkPageBreak();
                    doc.text(nLine, margin + 5, currentY);
                    currentY += 5.5;
                  });
                  yPosition = currentY;
                  lineIdx++;
                }
                // Regular paragraphs
                else {
                  const cleanText = trimmed
                    .replace(/\*\*/g, '')
                    .replace(/https?:\/\/[^\s]+/g, '[URL]')
                    .replace(/[🛠️📈🎯📋💰⚠️🧪✅🔬🔍👥🏭📄📚🔗]/g, '')
                    .replace(/[ØÜ¡ß]/g, '')
                    .replace(/[^\x20-\x7E\n\r\t]/g, '');
                  
                  if (cleanText) {
                    addText(cleanText, 11, false, [0, 0, 0]);
                  }
                  lineIdx++;
                }
              }
            }
          }
        } else {
          // Original code for non-table content (also handles markdown tables)
          const lines = content.split('\n');
          let lineIdx = 0;
          
          while (lineIdx < lines.length) {
            const line = lines[lineIdx];
            const trimmed = line.trim();
            
            if (!trimmed) {
              yPosition += 3;
              lineIdx++;
              continue;
            }

            // Check for markdown table (lines with | separator)
            if (trimmed.includes('|') && trimmed.split('|').filter(c => c.trim()).length > 1) {
              const { tableData, nextIdx } = parseMarkdownTable(lines, lineIdx);
              
              if (tableData && (tableData.headers.length > 0 || tableData.rows.length > 0)) {
                checkPageBreak(30);
                
                autoTable(doc, {
                  startY: yPosition,
                  head: tableData.headers.length > 0 ? [tableData.headers] : undefined,
                  body: tableData.rows,
                  margin: { left: margin, right: margin },
                  theme: 'striped',
                  headStyles: {
                    fillColor: [52, 73, 94],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    fontSize: 10
                  },
                  bodyStyles: {
                    fontSize: 9,
                    textColor: [0, 0, 0]
                  },
                  alternateRowStyles: {
                    fillColor: [245, 245, 245]
                  }
                });
                
                yPosition = doc.lastAutoTable.finalY + 8;
                lineIdx = nextIdx;
                continue;
              }
            }

            checkPageBreak();

            // Skip separator lines
            if (/^[=-]+$/.test(trimmed)) {
              lineIdx++;
              lineIdx++;
              continue;
            }

            // Headers (###, **)
            if (trimmed.startsWith('###') || (trimmed.startsWith('**') && trimmed.endsWith('**'))) {
              const headerText = trimmed.replace(/^###\s*/, '').replace(/^\*\*|\*\*$/g, '')
                .replace(/[ØÜ¡ß]/g, '')
                .replace(/[^\x20-\x7E\n\r\t]/g, '');
              addText(headerText, 12, true, [52, 73, 94]);
              lineIdx++;
            }
            // Bullet points
            else if (trimmed.startsWith('•') || trimmed.startsWith('-') || trimmed.startsWith('*')) {
              const bulletText = trimmed.substring(1).trim();
              const cleanText = bulletText.replace(/\*\*/g, '').replace(/https?:\/\/[^\s]+/g, '[URL]')
                .replace(/[ØÜ¡ß]/g, '')
                .replace(/[^\x20-\x7E\n\r\t]/g, '');
              doc.setFontSize(11);
              doc.setFont('helvetica', 'normal');
              doc.setTextColor(0, 0, 0);
              const bulletLines = doc.splitTextToSize(`• ${cleanText}`, maxWidth - 10);
              let currentY = yPosition;
              bulletLines.forEach((bLine, idx) => {
                checkPageBreak();
                doc.text(bLine, margin + (idx > 0 ? 12 : 5), currentY);
                currentY += 5.5;
              });
              yPosition = currentY;
              lineIdx++;
            }
            // Numbered lists
            else if (/^\d+\.\s/.test(trimmed)) {
              const cleanText = trimmed.replace(/\*\*/g, '').replace(/https?:\/\/[^\s]+/g, '[URL]')
                .replace(/[ØÜ¡ß]/g, '')
                .replace(/[^\x20-\x7E\n\r\t]/g, '');
              doc.setFontSize(11);
              doc.setFont('helvetica', 'normal');
              doc.setTextColor(0, 0, 0);
              const numLines = doc.splitTextToSize(cleanText, maxWidth - 10);
              let currentY = yPosition;
              numLines.forEach(nLine => {
                checkPageBreak();
                doc.text(nLine, margin + 5, currentY);
                currentY += 5.5;
              });
              yPosition = currentY;
              lineIdx++;
            }
            // Regular paragraphs
            else {
              const cleanText = trimmed
                .replace(/\*\*/g, '')
                .replace(/https?:\/\/[^\s]+/g, '[URL]')
                .replace(/[🛠️📈🎯📋💰⚠️🧪✅🔬🔍👥🏭📄📚🔗]/g, '')
                .replace(/[ØÜ¡ß]/g, '')
                .replace(/[^\x20-\x7E\n\r\t]/g, '');
              
              if (cleanText) {
                addText(cleanText, 11, false, [0, 0, 0]);
              }
              lineIdx++;
            }
          }
        }

        yPosition += 8;
      }
    }

    // Footer
    checkPageBreak(15);
    yPosition = pageHeight - 20;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(127, 140, 141);
    doc.text('Report generated using AI-powered analysis', pageWidth / 2, yPosition, { align: 'center' });

    // Generate filename
    const sanitizedTitle = (reportData.title || 'report')
      .replace(/[^a-z0-9]/gi, '_')
      .toLowerCase()
      .substring(0, 50);
    const fileName = `analysis_report_${sanitizedTitle}.pdf`;

    // Download
    doc.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('PDF Download Error:', error);
    throw error;
  }
};